import React from "react";
import { Input } from "reactstrap";

const NAVY = "#0A316E";

// Modal gövdesi için ortak alan stilleri (yalnızca ödeme alanlarını etkilesin diye
// `.pay-integration-fields` altına scope'landı — muhasebe formları etkilenmez).
export const PAY_FIELD_STYLES = `
  .pay-integration-fields .field-label {
    text-transform: uppercase; letter-spacing: .5px; font-size: 12px;
    font-weight: 600; color: #8b93a7; margin-bottom: .4rem;
  }
  .pay-integration-fields .form-control,
  .pay-integration-fields .form-select {
    background-color: #f6f7fb; border: 1.5px solid #e7eaf1; border-radius: 14px;
    padding: .7rem 1rem; color: #1f2937; font-size: 14.5px;
  }
  .pay-integration-fields .form-control:focus,
  .pay-integration-fields .form-select:focus {
    background-color: #fff; border-color: #b9c4de; box-shadow: 0 0 0 .18rem rgba(10,49,110,.08);
  }
  .pay-integration-fields .form-control::placeholder { color: #aab1c1; }
  .pay-integration-fields .field-hint {
    display: flex; align-items: center; gap: .35rem;
    font-size: 12.5px; color: #9aa2b1; margin-top: .4rem;
  }
  .pay-integration-fields .signup-box {
    background-color: #f6f7fb; border: 1px solid #e7eaf1; border-radius: 14px;
  }
`;

/**
 * Şema tabanlı ödeme sağlayıcı form alanları (modal gövdesi).
 * formik : ilgili sağlayıcı hook'unun formik'i · schema: PAYMENT_FIELD_SCHEMAS[id]
 *
 * showFields   : kimlik/anahtar alanları (Genel sekmesi / Bağla)
 * showHints    : alan altında ipucu metni (Bağla ekranı)
 * showEnv      : Ortam seçimi — Canlı/Test (Bağla ekranı)
 * showTestMode : Test Modu anahtarı (bağlı sağlayıcıda Genel sekmesi)
 * showInfo     : bilgilendirme kutusu (bildirim URL'i vb.)
 * showSignup   : sağlayıcının üye iş yeri başvurusu (şemada varsa) — dış bağlantı ya da
 *                panel içi form (signup.form)
 * onOpenApplication : panel içi başvuru formunu açar (pencere forma döner)
 */
export default function PaymentIntegrationFields({
    formik,
    schema,
    showFields = true,
    showHints = false,
    showEnv = false,
    showTestMode = false,
    showInfo = false,
    showSignup = false,
    onOpenApplication,
}) {
    if (!schema) return null;

    return (
        <div className="pay-integration-fields">
            <style>{PAY_FIELD_STYLES}</style>

            {showInfo && schema.infoAlert && (
                <div
                    className="mb-4 p-3"
                    style={{ backgroundColor: "#eaf3fb", borderRadius: 14, borderLeft: "4px solid #4a90d9" }}
                >
                    <div className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                        <i className={`bx ${schema.infoAlert.icon} me-1`} /> {schema.infoAlert.title}
                    </div>
                    <div className="small text-muted">{schema.infoAlert.body}</div>
                    {schema.infoAlert.code && (
                        <code className="d-inline-block mt-2" style={{ color: "#c2255c", wordBreak: "break-all" }}>
                            {schema.infoAlert.code}
                        </code>
                    )}
                </div>
            )}

            {/* Üye iş yeri başvurusu — yalnızca şemasında signup tanımlı sağlayıcıda
                (şu an PayTR) çizilir; diğerlerinde hiç render edilmez. */}
            {showSignup && schema.signup?.form && onOpenApplication && (
                <div className="signup-box d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 mb-4">
                    <div className="small text-muted">{schema.signup.note}</div>
                    <button
                        type="button"
                        className="btn btn-sm d-inline-flex align-items-center gap-1 fw-semibold"
                        style={{ borderRadius: 999, padding: ".45rem 1rem", backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                        onClick={onOpenApplication}
                    >
                        <i className="bx bx-edit-alt" />
                        {schema.signup.label}
                    </button>
                </div>
            )}

            {showSignup && schema.signup?.url && (
                <div className="signup-box d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 mb-4">
                    <div className="small text-muted">{schema.signup.note}</div>
                    <a
                        href={schema.signup.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm d-inline-flex align-items-center gap-1 fw-semibold"
                        style={{ borderRadius: 999, padding: ".45rem 1rem", backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                    >
                        <i className="bx bx-link-external" />
                        {schema.signup.label}
                    </a>
                </div>
            )}

            {showFields &&
                schema.fields.map((f) => {
                    const hasError = formik.touched[f.name] && Boolean(formik.errors[f.name]);
                    return (
                        <div className="mb-3" key={f.name}>
                            <label className="field-label d-block">{f.label}</label>
                            <Input
                                name={f.name}
                                type={f.type || "text"}
                                value={formik.values[f.name] || ""}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={hasError}
                                placeholder={f.placeholder || ""}
                            />
                            {hasError ? (
                                <div className="invalid-feedback d-block">{formik.errors[f.name]}</div>
                            ) : showHints && f.hint ? (
                                <div className="field-hint">
                                    <i className="bx bx-info-circle" />
                                    {f.hint}
                                </div>
                            ) : null}
                        </div>
                    );
                })}

            {showEnv && (
                <div className="mb-1">
                    <label className="field-label d-block">Ortam</label>
                    <Input
                        type="select"
                        value={formik.values.testMode ? "test" : "prod"}
                        onChange={(e) => formik.setFieldValue("testMode", e.target.value === "test")}
                    >
                        <option value="prod">Canlı (Production)</option>
                        <option value="test">Test (Sandbox)</option>
                    </Input>
                </div>
            )}

            {showTestMode && (
                <div className="p-3" style={{ backgroundColor: "#f6f7fb", borderRadius: 14, border: "1px solid #e7eaf1" }}>
                    <div
                        className="d-flex align-items-center"
                        onClick={() => formik.setFieldValue("testMode", !formik.values.testMode)}
                        style={{ cursor: "pointer", userSelect: "none" }}
                    >
                        <div className="form-check form-switch mb-0">
                            <Input type="switch" role="switch" checked={formik.values.testMode} readOnly style={{ cursor: "pointer" }} />
                        </div>
                        <div className="ms-2">
                            <span className="fw-bold d-block" style={{ color: NAVY }}>Test Modu</span>
                            <small className="text-muted">{schema.testModeLabel}</small>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
